import { Suspense, useEffect } from "react";
import { Router, Switch, Route, useLocation } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrandedPageLoader } from "@/components/public/BrandedPageLoader";
import { PageMeta } from "@/components/public/PageMeta";
import { lazyWithMinimumDelay } from "@/lib/lazyWithMinimumDelay";
import AdminLogin from "@/pages/AdminLogin";
import AdminChangePassword from "@/pages/AdminChangePassword";

const TEAM_LOADER_MESSAGE = "Preparing team access";

const Home = lazyWithMinimumDelay(() => import("@/pages/Home"));
const About = lazyWithMinimumDelay(() => import("@/pages/About"));
const Updates = lazyWithMinimumDelay(() => import("@/pages/Updates"));
const Feedback = lazyWithMinimumDelay(() => import("@/pages/Feedback"));
const Support = lazyWithMinimumDelay(() => import("@/pages/Support"));
const Faqs = lazyWithMinimumDelay(() => import("@/pages/Faqs"));

const lazyAdmin = () => import("@/pages/Admin");
const AdminAuthGate = lazyWithMinimumDelay(() => lazyAdmin().then((m) => ({ default: m.AdminAuthGate })));
const AdminRootRedirect = lazyWithMinimumDelay(() => lazyAdmin().then((m) => ({ default: m.AdminRootRedirect })));
const AdminChangePasswordGate = lazyWithMinimumDelay(() =>
  lazyAdmin().then((m) => ({ default: m.AdminChangePasswordGate })),
);
const AdminDashboard = lazyWithMinimumDelay(() => lazyAdmin().then((m) => ({ default: m.AdminDashboard })));

const queryClient = new QueryClient();

function AdminRouteFallback() {
  return <BrandedPageLoader message={TEAM_LOADER_MESSAGE} />;
}

function PublicRouteFallback() {
  return <BrandedPageLoader />;
}

function ScrollToTopOnRoute() {
  const [loc] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc]);
  return null;
}

function AdminOverviewRoute() {
  return (
    <Suspense fallback={<AdminRouteFallback />}>
      <AdminAuthGate>
        <Suspense fallback={<AdminRouteFallback />}>
          <AdminDashboard />
        </Suspense>
      </AdminAuthGate>
    </Suspense>
  );
}

function AdminIndexRoute() {
  return (
    <Suspense fallback={<AdminRouteFallback />}>
      <AdminAuthGate>
        <AdminRootRedirect />
      </AdminAuthGate>
    </Suspense>
  );
}

function AdminChangePasswordRoute() {
  return (
    <Suspense fallback={<AdminRouteFallback />}>
      <AdminChangePasswordGate>
        <AdminChangePassword />
      </AdminChangePasswordGate>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router hook={useHashLocation}>
          <PageMeta />
          <ScrollToTopOnRoute />
          <Switch>
            <Route path="/admin/login" component={AdminLogin} />
            <Route path="/admin/change-password" component={AdminChangePasswordRoute} />
            <Route path="/admin/overview" component={AdminOverviewRoute} />
            <Route path="/admin" component={AdminIndexRoute} />
            <Route path="/about">
              <Suspense fallback={<PublicRouteFallback />}>
                <About />
              </Suspense>
            </Route>
            <Route path="/updates">
              <Suspense fallback={<PublicRouteFallback />}>
                <Updates />
              </Suspense>
            </Route>
            <Route path="/feedback">
              <Suspense fallback={<PublicRouteFallback />}>
                <Feedback />
              </Suspense>
            </Route>
            <Route path="/support">
              <Suspense fallback={<PublicRouteFallback />}>
                <Support />
              </Suspense>
            </Route>
            <Route path="/faqs">
              <Suspense fallback={<PublicRouteFallback />}>
                <Faqs />
              </Suspense>
            </Route>
            <Route>
              <Suspense fallback={<PublicRouteFallback />}>
                <Home />
              </Suspense>
            </Route>
          </Switch>
        </Router>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
